/**
 * Settings Drawer Component
 * Reader settings organized in tabs: Display, Audio, Tadabbur
 */

import { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Settings, Volume2, Languages, Palette, Moon, Sun, Loader2, BookHeart, Monitor } from 'lucide-react';
import { useTheme } from '@/components/ui/theme-provider';
import { cn } from '@/lib/utils';
import type { ReaderSettings, Reciter, TranslationSource, TafsirSource } from '@/types/quran';
import { getReciters, getTranslations, getTafsirs } from '@/services/quran.service';

type SettingsTab = 'display' | 'audio' | 'tadabbur';

interface SettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  settings: ReaderSettings;
  onSettingsChange: (settings: ReaderSettings) => void;
  onReciterSelect?: (reciterId: string) => void;
  /** กำลังโหลดเสียงตัวอย่างอายะห์แรก (เมื่อเลือกกอรี) */
  previewAudioLoading?: boolean;
}

export function SettingsDrawer({
  open,
  onClose,
  settings,
  onSettingsChange,
  onReciterSelect,
  previewAudioLoading = false
}: SettingsDrawerProps) {
  const { theme, setTheme } = useTheme();
  const [reciters, setReciters] = useState<Reciter[]>([]);
  const [translations, setTranslations] = useState<TranslationSource[]>([]);
  const [_, setTafsirs] = useState<TafsirSource[]>([]);
  const [activeTab, setActiveTab] = useState<SettingsTab>('display');

  useEffect(() => {
    if (open) {
      getReciters().then(setReciters);
      getTranslations().then(setTranslations);
      getTafsirs('th').then(setTafsirs);
    }
  }, [open]);

  const updateSetting = <K extends keyof ReaderSettings>(key: K, value: ReaderSettings[K]) => {
    onSettingsChange({ ...settings, [key]: value });
  };

  const tabs: { key: SettingsTab; label: string; icon: React.ReactNode }[] = [
    { key: 'display', label: 'แสดงผล', icon: <Palette className="w-4 h-4" /> },
    { key: 'audio', label: 'เสียง', icon: <Volume2 className="w-4 h-4" /> },
    { key: 'tadabbur', label: 'ตะดับบุร', icon: <BookHeart className="w-4 h-4" /> },
  ];

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            ตั้งค่าการอ่าน
          </SheetTitle>
          <SheetDescription>
            ปรับแต่งการแสดงผลตามความต้องการ
          </SheetDescription>
        </SheetHeader>

        {/* Tab Navigation */}
        <div className="mt-4 flex rounded-lg bg-muted p-1 gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-xs font-medium transition-all',
                activeTab === tab.key
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="mt-5 space-y-6">
          {/* ═══ Display Tab ═══ */}
          {activeTab === 'display' && (
            <>
              {/* ── Theme Toggle ── */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  {theme === 'dark' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                  ธีม (Theme)
                </Label>
                <div className="flex gap-2">
                  <Button
                    variant={theme === 'light' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTheme('light')}
                    className="flex-1"
                  >
                    <Sun className="w-3.5 h-3.5 mr-1.5" />
                    สว่าง
                  </Button>
                  <Button
                    variant={theme === 'dark' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTheme('dark')}
                    className="flex-1"
                  >
                    <Moon className="w-3.5 h-3.5 mr-1.5" />
                    มืด
                  </Button>
                  <Button
                    variant={theme === 'system' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTheme('system')}
                    className="flex-1"
                  >
                    <Monitor className="w-3.5 h-3.5 mr-1.5" />
                    ระบบ
                  </Button>
                </div>
              </div>

              {/* ── Tajweed Toggle ── */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="show-tajweed" className="flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    ตัจวีด
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    แสดงสีตัจวีดในข้อความอาหรับ
                  </p>
                </div>
                <Switch
                  id="show-tajweed"
                  checked={settings.showTajweed ?? false}
                  onCheckedChange={(v) => updateSetting('showTajweed', v)}
                />
              </div>

              {/* ── Translation Toggle & Source ── */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="show-translation" className="flex items-center gap-2">
                      <Languages className="w-4 h-4" />
                      แสดงคำแปล
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      แสดงคำแปลใต้ข้อความอาหรับ
                    </p>
                  </div>
                  <Switch
                    id="show-translation"
                    checked={settings.showTranslation}
                    onCheckedChange={(v) => updateSetting('showTranslation', v)}
                  />
                </div>

                {settings.showTranslation && translations.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs">แหล่งคำแปล</Label>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {translations
                        .filter((t) => ['english', 'thai'].includes(t.languageName.toLowerCase()))
                        .slice(0, 10)
                        .map((t) => (
                          <button
                            key={t.id}
                            onClick={() => updateSetting('selectedTranslation', t.id)}
                            className={cn(
                              'whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                              settings.selectedTranslation === t.id
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted text-muted-foreground hover:bg-muted/80'
                            )}
                          >
                            {t.name}
                          </button>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ═══ Audio Tab ═══ */}
          {activeTab === 'audio' && (
            <>
              {/* ── Reciter ── */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Volume2 className="w-4 h-4" />
                  ผู้อ่าน (กอรี)
                </Label>
                <p className="text-sm text-muted-foreground">
                  เลือกผู้อ่านสำหรับฟังเสียง
                </p>
                {previewAudioLoading && (
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    กำลังโหลดเสียงตัวอย่าง...
                  </p>
                )}
                <div className="grid gap-2 max-h-[50vh] overflow-y-auto">
                  {reciters.length === 0 && !previewAudioLoading && (
                    <p className="text-sm text-muted-foreground">กำลังโหลด...</p>
                  )}
                  {reciters.map((r) => (
                    <Button
                      key={r.id}
                      variant={settings.selectedReciter === r.id ? 'default' : 'outline'}
                      disabled={previewAudioLoading}
                      onClick={() => {
                        updateSetting('selectedReciter', r.id);
                        onReciterSelect?.(r.id);
                      }}
                      className={cn(
                        'w-full justify-start text-left',
                        settings.selectedReciter === r.id && 'bg-primary text-primary-foreground'
                      )}
                      size="sm"
                    >
                      {r.name}
                      {r.style && (
                        <span className="ml-auto text-xs opacity-60">{r.style}</span>
                      )}
                    </Button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ═══ Tadabbur Tab ═══ */}
          {activeTab === 'tadabbur' && (
            <>
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Languages className="w-4 h-4" />
                  แหล่งคำแปลตะดับบุร
                </Label>
                <p className="text-sm text-muted-foreground">
                  เลือกแหล่งคำแปลที่จะแสดงในโหมดตะดับบุร
                </p>
                <div className="grid gap-2">
                  <button
                    onClick={() => updateSetting('tadabburTranslationSource', 'qf')}
                    className={cn(
                      'flex flex-col items-start gap-1 p-3 rounded-lg border text-left transition-colors',
                      (settings.tadabburTranslationSource ?? 'qf') === 'qf'
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-border hover:border-primary/50'
                    )}
                  >
                    <span className="text-sm font-medium">แปล QF (ค่าเริ่มต้น)</span>
                    <span className="text-xs text-muted-foreground">
                      Quran Foundation — คำแปลจาก quran.com
                    </span>
                  </button>
                  <button
                    onClick={() => updateSetting('tadabburTranslationSource', 'quranenc')}
                    className={cn(
                      'flex flex-col items-start gap-1 p-3 rounded-lg border text-left transition-colors',
                      settings.tadabburTranslationSource === 'quranenc'
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-border hover:border-primary/50'
                    )}
                  >
                    <span className="text-sm font-medium">QuranEnc มุคตะศ็อร</span>
                    <span className="text-xs text-muted-foreground">
                      ความหมายโดยสังเขปของอัลกุรอาน — quranenc.com
                    </span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default SettingsDrawer;
